import React, { useState, useEffect, useRef } from 'react';
import { FileText, Plus, X, Trash2, ChevronDown, ChevronUp, Package, AlertCircle, Check, Send, Clock, Upload, Loader2, Truck, CreditCard, Undo2, Pencil, Search, Repeat } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

// Searchable product picker — type any words ("salamander z 2") to filter;
// in-stock products sort first and show their available count.
const ProductPicker = ({ products, value, onChange, stockMap, showNote, index }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const sel = products.find(p => p.id === value);
  const labelOf = (p) => `${p.label}${showNote && p.variantNote ? ` (${p.variantNote})` : ''}`;
  const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
  const list = products
    .filter(p => {
      const hay = `${p.label} ${p.variantNote || ''}`.toLowerCase();
      return tokens.every(t => hay.includes(t));
    })
    .sort((a, b) => (((stockMap[b.id] || 0) > 0) - ((stockMap[a.id] || 0) > 0)) || (a.label || '').localeCompare(b.label || ''))
    .slice(0, 60);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); setQ(''); }}
        className={`h-9 w-full px-3 rounded-md bg-zinc-800 border border-zinc-700 text-sm text-left flex items-center justify-between gap-2 ${sel ? 'text-zinc-200' : 'text-zinc-500'}`}
        data-testid={`product-select-${index}`}
      >
        <span className="truncate">{sel ? labelOf(sel) : 'Select product...'}</span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 text-zinc-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute z-30 mt-1 w-full min-w-[20rem] bg-zinc-900 border border-zinc-700 rounded-md shadow-2xl">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
              <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search... e.g. salamander z 2"
                className="w-full bg-transparent text-sm text-zinc-200 placeholder-zinc-600 outline-none"
                data-testid={`product-search-${index}`}
              />
            </div>
            <div className="max-h-60 overflow-y-auto py-1">
              {list.map(p => {
                const stock = stockMap[p.id] || 0;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => { onChange(p.id); setOpen(false); setQ(''); }}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-zinc-800 ${p.id === value ? 'bg-zinc-800/60' : ''}`}
                    data-testid={`product-option-${p.id}`}
                  >
                    <span className="truncate text-zinc-200">{labelOf(p)}</span>
                    <span className={`shrink-0 text-[11px] ${stock > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>{stock} in stock</span>
                  </button>
                );
              })}
              {list.length === 0 && <div className="px-3 py-3 text-xs text-zinc-500">No products match "{q}"</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productStockMap, setProductStockMap] = useState({});  // productId -> available count
  const [disp, setDisp] = useState(null);
  const [customerPricing, setCustomerPricing] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [tvshRate, setTvshRate] = useState(0.18);  // configurable per invoice; default 18%
  const [pricePerKg, setPricePerKg] = useState(0); // single rate applied to every line
  const [isOfficial, setIsOfficial] = useState(true); // false → unofficial booklet invoice (U- number series)

  // PDF auto-fill state
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importBanner, setImportBanner] = useState(null); // { customerCandidate, unmatchedProductLabels, parsedTotals }
  
  // In-app confirm dialog (replaces window.confirm)
  // { title, description, confirmLabel, confirmVariant?, onConfirm }
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Date picker dialog — for ship / mark-paid / edit-dates flows. Lets the
  // operator backdate the action when recording historical data after the fact.
  // Shape: { title, description, fields: [{ key, label, defaultValue, min? }],
  //          confirmLabel, confirmVariant?, onConfirm(values) }
  const [dateDialog, setDateDialog] = useState(null);
  const [dateValues, setDateValues] = useState({});
  const [dateError, setDateError] = useState(null);
  const [dateSubmitting, setDateSubmitting] = useState(false);

  // YYYY-MM-DD for an ISO datetime or today by default
  const _toDateInput = (iso) => {
    if (!iso) return new Date().toISOString().slice(0, 10);
    try { return new Date(iso).toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
  };

  // Open the date dialog and seed each field's default value
  const openDateDialog = (config) => {
    const seed = {};
    for (const f of (config.fields || [])) seed[f.key] = f.defaultValue || '';
    setDateValues(seed);
    setDateError(null);
    setDateDialog(config);
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoicesRes, customersRes, productsRes, productStockRes, dispRes] = await Promise.all([
          fetch(`${API_URL}/api/invoices`),
          fetch(`${API_URL}/api/customers`),
          fetch(`${API_URL}/api/presets`),
          fetch(`${API_URL}/api/product-stock`),
          fetch(`${API_URL}/api/settings/display`)
        ]);
        
        if (invoicesRes.ok) setInvoices(await invoicesRes.json());
        if (dispRes.ok) setDisp(await dispRes.json());
        if (customersRes.ok) setCustomers(await customersRes.json());
        if (productsRes.ok) setProducts(await productsRes.json());
        if (productStockRes.ok) {
          const ps = await productStockRes.json();
          const map = {};
          for (const row of ps) {
            map[row.productId] = row.count;
          }
          setProductStockMap(map);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error loading data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL]);

  // Fetch customer pricing when customer is selected
  const fetchCustomerPricing = async (customerId) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/pricing`);
      if (response.ok) {
        const data = await response.json();
        const pricingMap = {};
        data.forEach(p => { pricingMap[p.productId] = p.pricePerUnit; });
        setCustomerPricing(prev => ({ ...prev, [customerId]: pricingMap }));
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  // Handle customer selection
  const handleCustomerChange = (customerId, opts = {}) => {
    const previousId = selectedCustomerId;
    setSelectedCustomerId(customerId);
    // Clear line items only when switching between customers manually — not on initial set
    // or during PDF import (where line items are populated afterwards).
    if (!opts.keepLineItems && previousId && previousId !== customerId) {
      setLineItems([]);
    }
    if (customerId && !customerPricing[customerId]) {
      fetchCustomerPricing(customerId);
    }
    // If the selected customer is TVSH-exempt, force the rate to 0.
    // Otherwise leave tvshRate untouched (resetForm handles the default 18%).
    const cust = customers.find(c => c.id === customerId);
    if (cust && cust.tvshExempt) {
      setTvshRate(0);
    }
  };

  // Add line item
  const addLineItem = () => {
    setLineItems([...lineItems, { _uid: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, productId: '', quantity: 1 }]);
  };

  // Flip an invoice between official Fatura PDF and simple booklet PDF
  const handleToggleOfficial = async (invoice) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoice.id}/set-official`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ official: invoice.official === false }),
      });
      if (res.ok) {
        const updated = await res.json();
        setInvoices(invoices.map(i => (i.id === updated.id ? updated : i)));
      }
    } catch (e) { console.error(e); }
  };

  // Prefill the form from an existing DRAFT invoice for editing
  const startEditInvoice = (invoice) => {
    setEditingInvoiceId(invoice.id);
    setSelectedCustomerId(invoice.customerId || '');
    setLineItems((invoice.lineItems || []).map((li, i) => ({
      _uid: `li-edit-${i}-${Math.random().toString(36).slice(2, 8)}`,
      productId: li.productId || '',
      quantity: li.quantity || 1,
    })));
    setPricePerKg(invoice.lineItems?.[0]?.pricePerKg ?? 0);
    setIsOfficial(invoice.official !== false);
    setTvshRate(invoice.tvshRate ?? 0.18);
    setNotes(invoice.notes || '');
    setDocumentUrl(invoice.documentUrl || '');
    setInvoiceDate((invoice.invoiceDate || '').slice(0, 10));
    setImportBanner(null);
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ===== Profile weight helpers (mirror backend formula) =====
  // Required sheet width applies the bend-expansion correction used in the
  // Factory page (validationUtils.js > calculateSheetWidth).
  const kgPerProfile = (product) => {
    if (!product) return 0;
    const base = parseFloat(product.base) || 0;
    const ls = parseFloat(product.leftSide) || 0;
    const rs = parseFloat(product.rightSide) || 0;
    const ll = parseFloat(product.leftLip) || 0;
    const rl = parseFloat(product.rightLip) || 0;
    const t = parseFloat(product.thickness) || 0;
    const numberOfAngles = 2 + (ll > 0 ? 1 : 0) + (rl > 0 ? 1 : 0);
    const expansion = numberOfAngles * 2 * t;
    const width = base + ls + rs + ll + rl - expansion;
    if (width <= 0 || t <= 0) return 0;
    return (width / 1000) * 6 * (t * 7.85 + 0.1); // substrate + Z100 zinc (100 g/m²)
  };

  const lineDerived = (item) => {
    const product = products.find(p => p.id === item.productId);
    const kgPer = kgPerProfile(product);
    const qty = parseInt(item.quantity) || 0;
    // Use the actual weight from the PDF when available — that's the real
    // shipped weight, not the theoretical preset weight. For manually-entered
    // lines, fall back to qty × theoretical kg/profile.
    const totalKg = (item._parsedTotalWeightKg != null && item._parsedTotalWeightKg > 0)
      ? parseFloat(item._parsedTotalWeightKg)
      : kgPer * qty;
    const ppk = parseFloat(pricePerKg) || 0;
    const lineSubtotal = totalKg * ppk;
    const pricePerProfile = qty > 0 ? lineSubtotal / qty : 0;
    return { kgPer, totalKg, pricePerProfile, lineSubtotal };
  };

  // Update line item
  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  // Remove line item
  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // Calculate totals — subtotal uses invoice-level pricePerKg × totalKg (TVSH-exclusive)
  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + lineDerived(item).lineSubtotal, 0);
    const tvshAmount = subtotal * tvshRate;
    const total = subtotal + tvshAmount;
    const totalCope = lineItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
    const totalKg = lineItems.reduce((sum, item) => sum + lineDerived(item).totalKg, 0);
    return { subtotal, tvshAmount, total, totalCope, totalKg };
  };

  // Create invoice
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedCustomerId) {
      setError('Please select a customer');
      return;
    }
    if (lineItems.length === 0) {
      setError('Add at least one product');
      return;
    }

    try {
      const payload = {
        customerId: selectedCustomerId,
        lineItems: lineItems.map(item => ({
          productId: item.productId,
          quantity: parseInt(item.quantity),
          pricePerKg: parseFloat(pricePerKg) || 0,
          // When the line came from a PDF, send the ACTUAL shipped weight so
          // the backend's lineTotal matches the invoiced amount exactly.
          actualTotalWeightKg: item._parsedTotalWeightKg || null,
        })),
        notes: notes || null,
        documentUrl: documentUrl || null,
        tvshRate: tvshRate,
        invoiceDate: invoiceDate || null,
        ...(editingInvoiceId ? {} : { official: isOfficial })
      };
      const response = await fetch(
        editingInvoiceId ? `${API_URL}/api/invoices/${editingInvoiceId}` : `${API_URL}/api/invoices`,
        {
          method: editingInvoiceId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const saved = await response.json();
        if (editingInvoiceId) {
          setInvoices(invoices.map(inv => inv.id === saved.id ? saved : inv));
        } else {
          setInvoices([saved, ...invoices]);
        }
        resetForm();
      } else {
        const data = await response.json().catch(() => null);
        const detail = data?.detail;
        if (detail?.error === 'stock_check_failed') {
          const lines = (detail.items || []).map(i => `• ${i.label}: need ${i.needed ?? '?'}, available ${i.available ?? 0}`);
          setError(`Not enough stock:\n${lines.join('\n')}`);
        } else if (detail?.error === 'invoice_not_draft') {
          setError('This invoice is shipped — unship it first, then edit.');
        } else {
          setError(typeof detail === 'string' ? detail : 'Error saving invoice');
        }
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setError('Network error');
    }
  };

  // Helper — bypasses fetch interceptors that consume the response body.
  const xhrJson = (method, url, body) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    const token = localStorage.getItem('auth_token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (body !== undefined) xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = () => {
      let data = null;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch (e) { console.debug('[xhrJson] non-JSON body:', e?.message); }
      resolve({ status: xhr.status, data, raw: xhr.responseText });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(body !== undefined ? JSON.stringify(body) : null);
  });

  const refreshInvoicesAndStock = async () => {
    const invRes = await xhrJson('GET', `${API_URL}/api/invoices`);
    if (invRes.status >= 200 && invRes.status < 300 && Array.isArray(invRes.data)) {
      setInvoices(invRes.data);
    }
    const stockRes = await xhrJson('GET', `${API_URL}/api/product-stock`);
    if (Array.isArray(stockRes.data)) {
      const map = {};
      for (const row of stockRes.data) map[row.productId] = row.count;
      setProductStockMap(map);
    }
  };

  // Format an error payload from the structured 409 responses (stock_check_failed_at_date / paid_before_shipped / shipped_date_before_production)
  const _formatBackdateError = (detail) => {
    if (!detail || typeof detail !== 'object') return null;
    if (detail.error === 'stock_check_failed_at_date') {
      const lines = (detail.items || []).map(i => `• ${i.product}: needed ${i.needed}, only ${i.available} produced by ${(i.shippedAt || '').slice(0, 10)}`);
      return `Stock wasn't available on that ship date:\n${lines.join('\n')}\nPick a later date or produce more stock first.`;
    }
    if (detail.error === 'shipped_date_before_production') {
      const lines = (detail.items || []).map(i => `• ${i.product} (${i.qty} pcs) produced ${(i.producedAt || '').slice(0, 10)}`);
      return `New ship date is earlier than when these batches were produced:\n${lines.join('\n')}`;
    }
    if (detail.error === 'paid_before_shipped') {
      return `Paid date (${(detail.paidAt || '').slice(0, 10)}) cannot be earlier than the shipment date (${(detail.shippedAt || '').slice(0, 10)}).`;
    }
    return null;
  };

  // Mark invoice as Shipped — deducts stock; date defaults to today, can be backdated
  const shipInvoice = (invoice) => {
    openDateDialog({
      title: 'Mark this invoice as shipped',
      description: `Stock will be deducted for invoice #${invoice.invoiceNumber}. Pick the date the goods actually left the warehouse — defaults to today.`,
      fields: [{ key: 'shippedAt', label: 'Shipment date', defaultValue: _toDateInput(invoice.invoiceDate || null), max: _toDateInput(null) }],
      confirmLabel: 'Mark Shipped',
      onConfirm: async ({ shippedAt }) => {
        const { status, data, raw } = await xhrJson('POST', `${API_URL}/api/invoices/${invoice.id}/process`, { effectiveAt: shippedAt });
        if (status >= 200 && status < 300) {
          await refreshInvoicesAndStock();
          return true;
        }
        const formatted = _formatBackdateError(data?.detail);
        throw new Error(formatted || data?.detail || raw || `Error marking as shipped (HTTP ${status})`);
      },
    });
  };

  // Revert to Draft — restores stock + clears shipped/paid flags
  const revertInvoice = (invoice) => {
    setConfirmDialog({
      title: 'Revert this invoice to draft?',
      description: `Stock will be restored for invoice #${invoice.invoiceNumber}. Shipped & paid dates will be cleared.`,
      confirmLabel: 'Revert to Draft',
      confirmVariant: 'destructive',
      onConfirm: async () => {
        try {
          const { status, data, raw } = await xhrJson('POST', `${API_URL}/api/invoices/${invoice.id}/unprocess`);
          if (status >= 200 && status < 300) {
            await refreshInvoicesAndStock();
          } else {
            const d = data?.detail;
            const msg = (d && typeof d === 'object') ? (d.message || JSON.stringify(d)) : (d || raw || `Error reverting (HTTP ${status})`);
            setError(msg);
          }
        } catch (e) {
          setError(e.message || 'Network error');
        }
      },
    });
  };

  // Mark invoice as Paid — date defaults to today, can be backdated
  const markPaid = (invoice) => {
    const shippedAtIso = invoice.shippedAt || invoice.processedAt;
    const minDate = shippedAtIso ? _toDateInput(shippedAtIso) : undefined;
    openDateDialog({
      title: 'Record payment received',
      description: `Pick the date payment was actually received for invoice #${invoice.invoiceNumber}. Must be on or after the shipment date.`,
      fields: [{ key: 'paidAt', label: 'Payment date', defaultValue: _toDateInput(null), min: minDate, max: _toDateInput(null) }],
      confirmLabel: 'Mark Paid',
      onConfirm: async ({ paidAt }) => {
        const { status, data, raw } = await xhrJson('POST', `${API_URL}/api/invoices/${invoice.id}/mark-paid`, { effectiveAt: paidAt });
        if (status >= 200 && status < 300) {
          await refreshInvoicesAndStock();
          return true;
        }
        const formatted = _formatBackdateError(data?.detail);
        throw new Error(formatted || data?.detail || raw || `Error marking paid (HTTP ${status})`);
      },
    });
  };

  // Edit shipped/paid dates on an already-processed invoice (retroactive fix)
  const editDates = (invoice) => {
    const fields = [];
    if (invoice.processed) {
      fields.push({
        key: 'shippedAt',
        label: 'Shipment date',
        defaultValue: _toDateInput(invoice.shippedAt || invoice.processedAt),
        max: _toDateInput(null),
      });
    }
    if (invoice.paid) {
      fields.push({
        key: 'paidAt',
        label: 'Payment date',
        defaultValue: _toDateInput(invoice.paidAt),
        max: _toDateInput(null),
      });
    }
    if (fields.length === 0) return;
    openDateDialog({
      title: 'Edit invoice dates',
      description: `Retroactively correct the dates on invoice #${invoice.invoiceNumber}. The new shipment date can't predate when the underlying stock was produced.`,
      fields,
      confirmLabel: 'Save dates',
      onConfirm: async (values) => {
        const payload = {};
        if (values.shippedAt) payload.shippedAt = values.shippedAt;
        if (values.paidAt) payload.paidAt = values.paidAt;
        const { status, data, raw } = await xhrJson('PUT', `${API_URL}/api/invoices/${invoice.id}/dates`, payload);
        if (status >= 200 && status < 300) {
          await refreshInvoicesAndStock();
          return true;
        }
        const formatted = _formatBackdateError(data?.detail);
        throw new Error(formatted || data?.detail || raw || `Error updating dates (HTTP ${status})`);
      },
    });
  };

  // Reverse Paid
  const unmarkPaid = (invoice) => {
    setConfirmDialog({
      title: 'Undo payment?',
      description: `Invoice #${invoice.invoiceNumber} will move back to "shipped" — payment date will be cleared.`,
      confirmLabel: 'Undo Payment',
      confirmVariant: 'destructive',
      onConfirm: async () => {
        try {
          const { status, data, raw } = await xhrJson('POST', `${API_URL}/api/invoices/${invoice.id}/unmark-paid`);
          if (status >= 200 && status < 300) {
            await refreshInvoicesAndStock();
          } else {
            setError(data?.detail || raw || `Error reverting payment (HTTP ${status})`);
          }
        } catch (e) {
          setError(e.message || 'Network error');
        }
      },
    });
  };

  // Delete invoice — backend refuses if the invoice is already shipped
  // (processed=True) because that would ghost-drain stock. In that case we
  // surface a friendly hint pointing at Revert-to-Draft / Undo Payment.
  const handleDelete = (invoice) => {
    setConfirmDialog({
      title: `Delete invoice #${invoice.invoiceNumber}?`,
      description: invoice.processed
        ? 'This invoice was shipped, so stock has already been deducted. Deleting is blocked to prevent ghost drains — un-ship it first (Revert to Draft) which restores stock, then delete.'
        : 'This cannot be undone.',
      confirmLabel: 'Delete',
      confirmVariant: 'destructive',
      onConfirm: async () => {
        try {
          const { status, data, raw } = await xhrJson('DELETE', `${API_URL}/api/invoices/${invoice.id}`);
          if (status >= 200 && status < 300) {
            setInvoices(invoices.filter(inv => inv.id !== invoice.id));
          } else {
            const d = data?.detail;
            const msg = (d && typeof d === 'object') ? (d.message || JSON.stringify(d)) : (d || raw || `HTTP ${status}`);
            setError(msg);
          }
        } catch (e) {
          setError(e.message || 'Network error');
        }
      },
    });
  };

  // Reset form
  const resetForm = () => {
    setEditingInvoiceId(null);
    setSelectedCustomerId('');
    setLineItems([]);
    setNotes('');
    setInvoiceDate('');
    setDocumentUrl('');
    setTvshRate(0.18);
    setPricePerKg(0);
    setIsOfficial(true);
    setImportBanner(null);
    setShowAddForm(false);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  // ===== PDF Auto-fill =====
  const norm = (s) => (s || '').toString().trim().toLowerCase();

  // Known brand keywords (case-insensitive substring match against the label)
  const BRAND_KEYWORDS = ['schuco', 'deceuninck', 'salamander', 'veka', 'baufens', 'worldplast', 'komm/troc', 'kommerling'];

  // Extract (brand, shape, thickness) from a label like "Profil per Schuco L 2mm".
  const parseSignature = (label, fallbackThickness = null) => {
    const lower = norm(label);
    const brand = BRAND_KEYWORDS.find(b => lower.includes(b)) || null;
    let shape = null;
    const shapeMatches = lower.match(/\b(l\/z\/t|l\/z|z\/t|l\/t|[lzt])\b/);
    if (shapeMatches) shape = shapeMatches[1];
    let thickness = null;
    const tm = lower.match(/(\d+\.?\d*)\s*mm/);
    if (tm) thickness = parseFloat(tm[1]);
    else if (fallbackThickness != null) thickness = parseFloat(fallbackThickness);
    return { brand, shape, thickness };
  };

  const sigMatches = (parsedSig, preset) => {
    const presetSig = parseSignature(preset.label, preset.thickness);
    if (parsedSig.brand && presetSig.brand && parsedSig.brand !== presetSig.brand) return false;
    if (parsedSig.shape && presetSig.shape) {
      const shapeCompat = parsedSig.shape === presetSig.shape
        || presetSig.shape.includes(parsedSig.shape)
        || parsedSig.shape.includes(presetSig.shape);
      if (!shapeCompat) return false;
    }
    if (parsedSig.thickness != null && presetSig.thickness != null) {
      if (Math.abs(parseFloat(presetSig.thickness) - parsedSig.thickness) > 0.05) return false;
    }
    return true;
  };

  const findCustomerMatch = (parsedCustomer) => {
    if (!parsedCustomer) return null;
    const name = norm(parsedCustomer.name);
    const company = norm(parsedCustomer.company);
    return customers.find(c =>
      (name && norm(c.name) === name) ||
      (company && norm(c.company) === company) ||
      (name && norm(c.company) === name) ||
      (company && norm(c.name) === company)
    ) || null;
  };

  // PDF → preset matcher.
  // Primary signal: brand + shape + thickness extracted from the printed label.
  // Confirmation: weight per profile (totalWeightKg / qty) is within 15% of the
  // preset's theoretical 6m weight — guards against picking the wrong thickness
  // when the label is ambiguous (e.g. "Schuco L" with no thickness printed).
  // Returns { match: Product|null }. Strict — never invents a candidates list.
  const findProductMatch = (parsedLine) => {
    const parsedSig = parseSignature(parsedLine.productLabel, parsedLine.dimensions?.thickness);
    const qty = parseInt(parsedLine.quantity) || 0;
    const totalKg = parseFloat(parsedLine.totalWeightKg);
    const parsedKgPer = (qty > 0 && isFinite(totalKg) && totalKg > 0) ? totalKg / qty : null;

    // Score each preset that passes the label signature filter
    const candidates = products
      .filter(p => sigMatches(parsedSig, p))
      .map(p => {
        const presetKg = kgPerProfile(p);
        const delta = (parsedKgPer != null && presetKg > 0)
          ? Math.abs(presetKg - parsedKgPer) / parsedKgPer
          : null;
        return { preset: p, delta };
      })
      .sort((a, b) => {
        // Prefer those within 15% weight tolerance; among those, pick the smallest delta
        const aWithin = a.delta != null && a.delta <= 0.15;
        const bWithin = b.delta != null && b.delta <= 0.15;
        if (aWithin && !bWithin) return -1;
        if (!aWithin && bWithin) return 1;
        if (a.delta == null && b.delta == null) return 0;
        if (a.delta == null) return 1;
        if (b.delta == null) return -1;
        return a.delta - b.delta;
      });

    if (candidates.length === 0) return { match: null };

    const best = candidates[0];
    // If a weight comparison was possible, only accept when within 30%
    // (loose enough to handle preset-dimension imprecision but strict enough
    // to reject obviously wrong picks like 0.8mm vs 2mm — those differ ~2.5x).
    if (best.delta != null && best.delta > 0.30) return { match: null };
    return { match: best.preset };
  };

  const createCustomerFromParsed = async (pc, opts = {}) => {
    try {
      // Infer TVSH-exempt status: if the PDF explicitly had tvshRate = 0, the customer is likely export/exempt
      const inferredExempt = opts.tvshExempt ?? (typeof opts.parsedTvshRate === 'number' && opts.parsedTvshRate === 0);
      const body = {
        name: pc.name || pc.company || 'Unknown',
        company: pc.company || null,
        address: pc.address || null,
        taxId: pc.taxId || null,
        phone: pc.phone || null,
        email: pc.email || null,
        tvshExempt: inferredExempt,
      };
      const res = await fetch(`${API_URL}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to create customer');
      const created = await res.json();
      setCustomers(prev => [...prev, created]);
      handleCustomerChange(created.id);
      setImportBanner(b => b ? { ...b, customerCandidate: null, autoCreatedCustomer: created } : null);
      return created;
    } catch (e) {
      setError(e.message || 'Failed to create customer');
      return null;
    }
  };

  const handleImportPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    setError(null);
    setImportBanner(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${API_URL}/api/parse/invoice`, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || 'Parse failed');
      }
      const data = await res.json();
      const p = data.parsed || {};

      setShowAddForm(true);
      setDocumentUrl(data.documentUrl || '');
      setNotes(p.notes || '');
      // Respect the TVSH rate from the PDF when explicitly present (incl. 0%)
      if (typeof p.tvshRate === 'number') {
        setTvshRate(p.tvshRate);
      } else {
        setTvshRate(0.18);
      }

      // Customer match — if not found, auto-create and select them
      let matched = findCustomerMatch(p.customer);
      let autoCreated = null;
      if (matched) {
        handleCustomerChange(matched.id);
      } else if (p.customer && (p.customer.name || p.customer.company)) {
        autoCreated = await createCustomerFromParsed(p.customer, { parsedTvshRate: p.tvshRate });
      }

      // Line items — strict weight-based match
      const unmatched = [];
      const filledItems = (p.lineItems || []).map(li => {
        const { match } = findProductMatch(li);
        if (!match) unmatched.push(li.productLabel);
        return {
          productId: match ? match.id : '',
          quantity: parseInt(li.quantity) || 1,
          _parsedLabel: li.productLabel || '',
          _parsedTotalWeightKg: parseFloat(li.totalWeightKg) || null,
        };
      });
      setLineItems(filledItems);

      // Use invoice-level price/kg from PDF if all line items resolve to the same value
      if (p.lineItems && p.lineItems.length > 0 && filledItems.every(fi => fi.productId)) {
        const perKgs = (p.lineItems || []).map(li => {
          const tw = parseFloat(li.totalWeightKg);
          const ppu = parseFloat(li.pricePerUnit);
          const qty = parseInt(li.quantity);
          if (tw > 0 && ppu > 0 && qty > 0) return (ppu * qty) / tw;
          return null;
        }).filter(Boolean);
        if (perKgs.length > 0) {
          const avg = perKgs.reduce((a, b) => a + b, 0) / perKgs.length;
          setPricePerKg(Number(avg.toFixed(4)));
        }
      }

      setImportBanner({
        customerCandidate: (matched || autoCreated) ? null : p.customer,
        autoCreatedCustomer: autoCreated,
        unmatchedProductLabels: unmatched,
        parsedTotals: { subtotal: p.subtotal, tvshAmount: p.tvshAmount, total: p.total, invoiceNumber: p.invoiceNumber, date: p.invoiceDate },
      });
    } catch (err) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  // Format helpers for the timeline
  const _fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso.slice(0, 10); }
  };
  const _daysBetween = (a, b) => {
    if (!a || !b) return null;
    try {
      const d = (new Date(b) - new Date(a)) / 86400000;
      return d >= 0 ? Math.round(d) : null;
    } catch { return null; }
  };

  // Timeline strip: Shipped → Paid with durations.
  // `created_at` was removed from the UI — user said "we create same day we ship,
  // no meaningful distinction". The field is kept in the DB for pure audit/insert
  // tracking but never surfaced.
  const InvoiceTimeline = ({ invoice }) => {
    const shippedAt = invoice.shippedAt || invoice.processedAt;
    const paidAt = invoice.paidAt;
    const daysToPay = _daysBetween(shippedAt, paidAt);

    const Pill = ({ icon: Icon, label, date, done }) => (
      <div className={`flex items-start gap-2 ${done ? '' : 'opacity-40'}`}>
        <div className={`mt-0.5 rounded-full p-1 ${done ? 'bg-emerald-600/30 text-emerald-300' : 'bg-zinc-800 text-zinc-500'}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="leading-tight">
          <div className={`text-xs font-medium ${done ? 'text-zinc-200' : 'text-zinc-500'}`}>{label}</div>
          <div className={`text-[11px] ${done ? 'text-zinc-400' : 'text-zinc-600'}`}>{done ? (_fmtDate(date) || '—') : 'Pending'}</div>
        </div>
      </div>
    );
    const Arrow = ({ label }) => (
      <div className="flex items-center text-[10px] text-zinc-500 px-2">
        <div className="border-t border-zinc-700 flex-1 w-6" />
        {label && <span className="px-1.5 whitespace-nowrap">{label}</span>}
        <div className="border-t border-zinc-700 flex-1 w-6" />
      </div>
    );

    return (
      <div className="mb-4 p-3 bg-zinc-800/30 rounded-lg flex flex-wrap items-center gap-x-1 gap-y-2" data-testid={`invoice-timeline-${invoice.id}`}>
        <Pill icon={Truck} label="Shipped" date={shippedAt} done={!!shippedAt} />
        <Arrow label={daysToPay != null ? `${daysToPay}d` : null} />
        <Pill icon={CreditCard} label="Paid" date={paidAt} done={!!paidAt} />
      </div>
    );
  };

  // Status badge — derives a single label from (paid, shipped/processed, status)
  const StatusBadge = ({ invoice }) => {
    const paid = !!invoice.paid;
    const shipped = !!invoice.processed;
    const key = paid ? 'paid' : shipped ? 'shipped' : (invoice.status || 'draft');
    const styles = {
      draft: 'bg-zinc-700 text-zinc-300',
      sent: 'bg-blue-900/50 text-blue-400',
      shipped: 'bg-amber-900/50 text-amber-300',
      paid: 'bg-emerald-900/50 text-emerald-300',
      processed: 'bg-amber-900/50 text-amber-300',
    };
    const labels = { draft: 'Draft', sent: 'Sent', shipped: 'Shipped', paid: 'Paid', processed: 'Shipped' };
    const icons = { draft: Clock, sent: Send, shipped: Truck, paid: Check, processed: Truck };
    const Icon = icons[key] || Clock;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${styles[key] || styles.draft}`}>
        <Icon className="w-3 h-3" />
        {labels[key] || key}
      </span>
    );
  };

  const { subtotal, tvshAmount, total, totalCope, totalKg } = calculateTotals();

  // Stock / product validation across all line items
  const stockIssues = lineItems.map((item) => {
    if (!item.productId) {
      // From a PDF import with no weight-based match → user must pick or add the profile
      const fromImport = !!item._parsedLabel;
      return fromImport ? { type: 'no_match', label: item._parsedLabel } : { type: 'no_product' };
    }
    const available = productStockMap[item.productId] ?? 0;
    if ((item.quantity || 0) > available) {
      const product = products.find(p => p.id === item.productId);
      return { type: 'insufficient_stock', available, needed: item.quantity, label: product?.label || '' };
    }
    return null;
  });
  const hasBlockingIssue = stockIssues.some(Boolean);
  const noMatchLabels = stockIssues.filter(s => s?.type === 'no_match').map(s => s.label);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-emerald-500" />
          <h1 className="text-xl sm:text-2xl font-bold text-white">Invoices</h1>
          <span className="text-sm text-zinc-500">({invoices.length})</span>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => importFileRef.current?.click()}
            variant="outline"
            disabled={importing}
            className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 gap-2"
            data-testid="import-invoice-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span className="hidden sm:inline">{importing ? 'Parsing…' : 'Import PDF'}</span>
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="add-invoice-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Invoice</span>
          </Button>
          <input
            ref={importFileRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={handleImportPdf}
            data-testid="invoice-pdf-input"
          />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-900/30 border border-red-800 rounded-lg text-red-400" data-testid="error-message">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add Invoice Form */}
      {showAddForm && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 mb-6" data-testid="invoice-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{editingInvoiceId ? `Edit Invoice #${invoices.find(i => i.id === editingInvoiceId)?.invoiceNumber || ''}` : 'New Invoice'}</h2>
            <button onClick={resetForm} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Import banner */}
          {importBanner && (
            <div className="mb-4 p-3 bg-purple-950/30 border border-purple-900/50 rounded-lg space-y-2" data-testid="import-banner">
              <div className="flex items-center gap-2 text-sm text-purple-300 font-medium">
                <Upload className="w-4 h-4" />
                Auto-filled from PDF
              </div>
              {importBanner.parsedTotals && (
                <div className="text-xs text-zinc-400">
                  PDF — Invoice #: <span className="text-zinc-200">{importBanner.parsedTotals.invoiceNumber || '—'}</span>
                  {' · '}Date: <span className="text-zinc-200">{importBanner.parsedTotals.date || '—'}</span>
                  {' · '}Subtotal: <span className="text-zinc-200">{importBanner.parsedTotals.subtotal ?? '—'}</span>
                  {' · '}TVSH: <span className="text-zinc-200">{importBanner.parsedTotals.tvshAmount ?? '—'}</span>
                  {' · '}Total: <span className="text-zinc-200">{importBanner.parsedTotals.total ?? '—'}</span>
                </div>
              )}
              {importBanner.autoCreatedCustomer && (
                <div className="text-xs text-emerald-300" data-testid="auto-created-customer-note">
                  ✓ Auto-created customer: <strong>{importBanner.autoCreatedCustomer.name}</strong>
                  {importBanner.autoCreatedCustomer.tvshExempt && ' (TVSH-exempt)'}
                </div>
              )}
              {importBanner.customerCandidate && (
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-amber-400">⚠ Customer not in database:</span>
                  <span className="text-zinc-300">
                    {importBanner.customerCandidate.name || importBanner.customerCandidate.company}
                    {importBanner.customerCandidate.taxId ? ` · NUI ${importBanner.customerCandidate.taxId}` : ''}
                  </span>
                  <button
                    onClick={() => createCustomerFromParsed(importBanner.customerCandidate)}
                    className="ml-auto text-emerald-400 hover:text-emerald-300 underline"
                    data-testid="create-customer-from-pdf-btn"
                  >
                    Create customer
                  </button>
                </div>
              )}
              {importBanner.unmatchedProductLabels?.length > 0 && (
                <div className="text-xs">
                  <span className="text-amber-400">⚠ Unmatched line items (pick a product manually):</span>
                  <ul className="list-disc pl-5 text-zinc-400">
                    {importBanner.unmatchedProductLabels.map((l) => <li key={l}>{l}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Selection + Invoice date */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
              <div>
                <Label className="text-zinc-400 text-sm">Customer *</Label>
                <select
                  value={selectedCustomerId}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-200"
                  data-testid="invoice-customer-select"
                >
                  <option value="">Select customer...</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.company ? `(${customer.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Invoice date</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="h-10 bg-zinc-800 border-zinc-700 text-zinc-200 sm:w-44"
                  data-testid="invoice-date-input"
                />
                <p className="text-[10px] text-zinc-600 mt-0.5">Empty = today. Past dates allowed.</p>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-400 text-sm">Products</Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={addLineItem}
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Product
                </Button>
              </div>

              {lineItems.length === 0 ? (
                <p className="text-sm text-zinc-600 py-2">No products added</p>
              ) : (
                <div className="space-y-2">
                  {lineItems.map((item, index) => {
                    const { kgPer, totalKg: lineKg, pricePerProfile, lineSubtotal } = lineDerived(item);
                    // Stable UID for React reconciliation — falls back to
                    // index for items imported from PDF (which don't get a
                    // client uid). Adding it lazily on first re-render keeps
                    // the fix backward-compatible.
                    if (!item._uid) item._uid = `li-idx-${index}-${item.productId || 'blank'}`;
                    return (
                    <div key={item._uid} className="flex gap-2 items-center p-2 bg-zinc-800/50 rounded-lg" data-testid={`line-item-${index}`}>
                      <div className="flex-1 flex flex-col gap-1">
                        {item._parsedLabel && (
                          <span
                            className={item.productId ? "text-[11px] text-zinc-400" : "text-[11px] text-amber-300/90"}
                            data-testid={`parsed-label-hint-${index}`}
                          >
                            {item.productId ? 'From PDF' : 'From PDF — no match, pick or add the profile'}: "{item._parsedLabel}"
                            {item._parsedTotalWeightKg && (
                              <span className="ml-1 text-zinc-500">
                                · actual {item._parsedTotalWeightKg.toFixed(2)} kg
                                {(parseInt(item.quantity) || 0) > 0 && (
                                  <> ({(item._parsedTotalWeightKg / (parseInt(item.quantity) || 1)).toFixed(2)} kg/profile)</>
                                )}
                              </span>
                            )}
                          </span>
                        )}
                        {item.productId && item._parsedTotalWeightKg && kgPer > 0 && (() => {
                          const parsedKgPer = item._parsedTotalWeightKg / (parseInt(item.quantity) || 1);
                          const drift = Math.abs(parsedKgPer - kgPer) / parsedKgPer;
                          if (drift > 0.05) {
                            return (
                              <span className="text-[11px] text-amber-400" data-testid={`weight-drift-${index}`}>
                                ⚠ Preset weight {kgPer.toFixed(2)} kg/profile differs from invoice's {parsedKgPer.toFixed(2)} kg by {(drift * 100).toFixed(1)}%. Using invoice weight for the total.
                              </span>
                            );
                          }
                          return null;
                        })()}
                        {stockIssues[index]?.type === 'no_match' && (
                          <span className="text-[11px] text-rose-400" data-testid={`no-product-error-${index}`}>
                            ✕ No profile matches this weight. Add it in the Factory before saving.
                          </span>
                        )}
                        {stockIssues[index]?.type === 'insufficient_stock' && (
                          <span className="text-[11px] text-rose-400" data-testid={`stock-error-${index}`}>
                            ✕ Only {stockIssues[index].available} in stock — need {stockIssues[index].needed}
                          </span>
                        )}
                        {item.productId && !stockIssues[index] && (
                          <span className="text-[11px] text-emerald-400/80" data-testid={`stock-ok-${index}`}>
                            ✓ {productStockMap[item.productId] ?? 0} in stock · {kgPer.toFixed(2)} kg/profile · {lineKg.toFixed(2)} kg total
                          </span>
                        )}
                        <ProductPicker
                          products={products}
                          value={item.productId}
                          onChange={(pid) => updateLineItem(index, 'productId', pid)}
                          stockMap={productStockMap}
                          showNote={!!disp?.variantNoteInvoices}
                          index={index}
                        />
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-zinc-500 uppercase">Copë</span>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                          className="w-16 h-9 bg-zinc-800 border-zinc-700 text-zinc-200 text-sm text-center"
                          placeholder="Qty"
                          data-testid={`qty-${index}`}
                        />
                      </div>
                      <div className="flex flex-col items-end w-24" data-testid={`derived-price-profile-${index}`}>
                        <span className="text-[10px] text-zinc-500 uppercase">€/copë</span>
                        <span className="text-sm text-zinc-300 h-9 flex items-center">
                          {pricePerProfile > 0 ? `€${pricePerProfile.toFixed(2)}` : '—'}
                        </span>
                      </div>
                      <div className="flex flex-col items-end w-24" data-testid={`line-subtotal-${index}`}>
                        <span className="text-[10px] text-zinc-500 uppercase">Totali</span>
                        <span className="text-sm text-emerald-300 h-9 flex items-center font-medium">
                          €{lineSubtotal.toFixed(2)}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Invoice-level price/kg — applies to every line */}
            <div className="bg-zinc-800/30 rounded-lg p-3 flex items-center gap-3" data-testid="invoice-price-per-kg-row">
              <Label className="text-zinc-400 text-sm whitespace-nowrap">Çmimi për KG (pa TVSH) *</Label>
              <Input
                type="number"
                step="0.0001"
                min="0"
                value={pricePerKg}
                onChange={(e) => setPricePerKg(parseFloat(e.target.value) || 0)}
                className="w-32 h-9 bg-zinc-800 border-zinc-700 text-zinc-200 text-right"
                placeholder="0.95"
                data-testid="invoice-price-per-kg"
              />
              <span className="text-xs text-zinc-500">Applied to every line.</span>
            </div>

            {/* Official vs Unofficial — picked at creation so unofficial invoices
                get a U- internal number and never consume the fiscal sequence */}
            {!editingInvoiceId && (
              <div className="bg-zinc-800/30 rounded-lg p-3 flex items-center gap-3" data-testid="invoice-official-row">
                <Label className="text-zinc-400 text-sm whitespace-nowrap">Lloji i faturës</Label>
                <div className="flex rounded-md overflow-hidden border border-zinc-700">
                  <button
                    type="button"
                    data-testid="invoice-type-official"
                    onClick={() => setIsOfficial(true)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${isOfficial ? 'bg-emerald-600/30 text-emerald-300' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}
                  >
                    Zyrtare (Fatura)
                  </button>
                  <button
                    type="button"
                    data-testid="invoice-type-unofficial"
                    onClick={() => setIsOfficial(false)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${!isOfficial ? 'bg-amber-600/30 text-amber-300' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}
                  >
                    Jozyrtare (Bllok)
                  </button>
                </div>
                <span className="text-xs text-zinc-500">
                  {isOfficial ? 'Numri fiskal mujor (YYMM-NNNN).' : 'Numër i brendshëm U- — nuk prek sekuencën zyrtare.'}
                </span>
              </div>
            )}

            {/* Totals */}
            {lineItems.length > 0 && (
              <div className="border-t border-zinc-800 pt-3 space-y-1" data-testid="invoice-totals">
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>Gjithsej Copë / Sasia KG:</span>
                  <span className="text-zinc-400">
                    <span data-testid="total-cope">{totalCope}</span>
                    {' copë · '}
                    <span data-testid="total-kg">{totalKg.toFixed(2)}</span>
                    {' kg'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Nëntotali (pa TVSH):</span>
                  <span className="text-zinc-300" data-testid="invoice-subtotal">€{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-1.5 text-zinc-500">
                    <span>TVSH</span>
                    {(() => {
                      const selectedCust = customers.find(c => c.id === selectedCustomerId);
                      const isExempt = !!selectedCust?.tvshExempt;
                      return (
                        <>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="1"
                            value={isExempt ? 0 : tvshRate}
                            disabled={isExempt}
                            onChange={(e) => setTvshRate(parseFloat(e.target.value) || 0)}
                            className={`w-16 h-6 px-1.5 text-xs bg-zinc-800 border-zinc-700 text-zinc-200 ${isExempt ? 'opacity-60 cursor-not-allowed' : ''}`}
                            data-testid="invoice-tvsh-rate"
                            title={isExempt ? 'Customer is TVSH-exempt (rate locked at 0)' : 'TVSH rate as decimal (0.18 = 18%, 0 = exempt)'}
                          />
                          <span>({((isExempt ? 0 : tvshRate) * 100).toFixed(0)}%{isExempt ? ' · exempt' : ''}):</span>
                        </>
                      );
                    })()}
                  </div>
                  <span className="text-zinc-300">€{tvshAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-semibold">
                  <span className="text-zinc-400">Totali për Pagesë:</span>
                  <span className="text-emerald-400" data-testid="invoice-total">€{total.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-zinc-400 text-sm">Notes</Label>
              <Input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-200"
                placeholder="Additional notes..."
              />
            </div>

            {/* Stock / product validation banner */}
            {hasBlockingIssue && (
              <div className="p-3 bg-rose-950/30 border border-rose-900/50 rounded-lg text-sm text-rose-300" data-testid="stock-blocking-banner">
                ✕ Cannot save: some line items have no matching product or insufficient stock. Fix the lines marked in red above.
                {noMatchLabels.length > 0 && (
                  <div className="mt-1 text-xs text-rose-200/80">
                    Add these profiles in the Factory first: {noMatchLabels.map(l => `"${l}"`).join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetForm} className="border-zinc-700 text-zinc-400">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={hasBlockingIssue || lineItems.length === 0 || !(parseFloat(pricePerKg) > 0)}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:cursor-not-allowed"
                data-testid="invoice-submit-btn"
              >
                {editingInvoiceId ? 'Save Changes' : 'Create Invoice'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Invoices List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">No invoices yet</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="invoices-list">
          {invoices.map(invoice => {
            const isExpanded = expandedInvoice === invoice.id;

            return (
              <div key={invoice.id} className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden" data-testid={`invoice-card-${invoice.id}`}>
                {/* Invoice Header */}
                <div className="flex items-center justify-between p-4">
                  <button
                    onClick={() => setExpandedInvoice(isExpanded ? null : invoice.id)}
                    className="flex-1 flex items-center gap-4 text-left"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-white">#{invoice.invoiceNumber}</span>
                        {invoice.official === false && (
                          <span className="text-[10px] uppercase tracking-wide text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded" data-testid={`invoice-booklet-badge-${invoice.id}`}>
                            B
                          </span>
                        )}
                        <StatusBadge invoice={invoice} />
                      </div>
                      <div className="text-sm text-zinc-500">
                        {invoice.customerName} {invoice.customerCompany ? `• ${invoice.customerCompany}` : ''}
                      </div>
                    </div>
                  </button>

                  <div className="flex items-center gap-4">
                    <span className="text-lg font-bold text-emerald-400">€{invoice.total.toFixed(2)}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Carry the auth token in the query string so
                          // direct-navigation works across DB environments.
                          const t = localStorage.getItem('auth_token');
                          const sep = t ? `?token=${encodeURIComponent(t)}` : '';
                          window.open(`${API_URL}/api/invoices/${invoice.id}/pdf${sep}`, '_blank', 'noopener,noreferrer');
                        }}
                        className="h-8 px-2 text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 gap-1"
                        data-testid={`invoice-pdf-${invoice.id}`}
                        title="Shkarko Fatura (PDF)"
                      >
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline text-xs">PDF</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleOfficial(invoice)}
                        className={`h-8 w-8 p-0 hover:bg-zinc-700 ${invoice.official === false ? 'text-amber-400 hover:text-zinc-300' : 'text-zinc-400 hover:text-amber-400'}`}
                        title={invoice.official === false ? 'Booklet PDF — click for the standard Fatura' : 'Standard Fatura — click for the simple booklet PDF'}
                        data-testid={`invoice-flip-${invoice.id}`}
                      >
                        <Repeat className="w-4 h-4" />
                      </Button>
                      {!invoice.processed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditInvoice(invoice)}
                          className="h-8 w-8 p-0 text-zinc-400 hover:text-amber-400 hover:bg-zinc-700"
                          title="Edit draft invoice"
                          data-testid={`invoice-edit-${invoice.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(invoice)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400 hover:bg-zinc-700"
                        data-testid={`invoice-delete-${invoice.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <button onClick={() => setExpandedInvoice(isExpanded ? null : invoice.id)} className="p-2 text-zinc-400">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-4" data-testid={`invoice-details-${invoice.id}`}>
                    {/* Lifecycle Timeline */}
                    <InvoiceTimeline invoice={invoice} />

                    {/* Status Actions — Draft → Shipped → Paid */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {!invoice.processed && (
                        <Button
                          size="sm"
                          onClick={() => shipInvoice(invoice)}
                          className="bg-amber-600 hover:bg-amber-700 text-xs gap-1"
                          data-testid={`invoice-ship-${invoice.id}`}
                        >
                          <Truck className="w-3 h-3" /> Mark Shipped
                        </Button>
                      )}
                      {invoice.processed && !invoice.paid && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => markPaid(invoice)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-xs gap-1"
                            data-testid={`invoice-mark-paid-${invoice.id}`}
                          >
                            <CreditCard className="w-3 h-3" /> Mark Paid
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revertInvoice(invoice)}
                            className="border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs gap-1"
                            data-testid={`invoice-revert-${invoice.id}`}
                          >
                            <Undo2 className="w-3 h-3" /> Revert to Draft
                          </Button>
                        </>
                      )}
                      {invoice.paid && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unmarkPaid(invoice)}
                          className="border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs gap-1"
                          data-testid={`invoice-unmark-paid-${invoice.id}`}
                        >
                          <Undo2 className="w-3 h-3" /> Undo Payment
                        </Button>
                      )}
                      {(invoice.processed || invoice.paid) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => editDates(invoice)}
                          className="border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs gap-1"
                          data-testid={`invoice-edit-dates-${invoice.id}`}
                        >
                          <Clock className="w-3 h-3" /> Edit Dates
                        </Button>
                      )}
                    </div>

                    {/* Line Items */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-zinc-400 mb-2">Products</h4>
                      <div className="space-y-1">
                        {invoice.lineItems.map((item, idx) => (
                          <div key={`${item.productId || item.productLabel}-${idx}`} className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-zinc-500" />
                              <span className="text-sm text-zinc-300">
                                {item.productLabel}
                                {disp?.variantNoteInvoices && products.find(p => p.id === item.productId)?.variantNote ? (
                                  <span className="ml-1 text-xs text-zinc-500">({products.find(p => p.id === item.productId).variantNote})</span>
                                ) : null}
                              </span>
                              {item.productSpecs && (
                                <span className="text-xs text-zinc-600">
                                  ({Math.round((item.productSpecs.sheetWidth ?? 0) * 10) / 10}mm × {item.productSpecs.thickness}mm)
                                </span>
                              )}
                              <span className="text-xs text-zinc-500">x{item.quantity}</span>
                              {item.weightKg && (
                                <span className="text-xs text-amber-600">({item.weightKg.toFixed(1)} kg)</span>
                              )}
                            </div>
                            <div className="text-sm text-zinc-400 text-right">
                              {item.pricePerKg != null && (
                                <div className="text-[11px] text-zinc-500">€{item.pricePerKg.toFixed(3)}/kg · €{(item.pricePerUnit ?? 0).toFixed(2)}/copë</div>
                              )}
                              <span className="text-zinc-200">€{item.lineTotal.toFixed(2)}</span>
                              {item.cogsTotal != null && (
                                <div className="text-[11px] mt-0.5 flex flex-col items-end" data-testid={`line-cogs-${item.productId}`}>
                                  <span className="text-zinc-500">COGS €{item.cogsTotal.toFixed(2)}</span>
                                  {item.profit != null && (
                                    <span className={item.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                                      Profit {item.profit >= 0 ? '+' : ''}€{item.profit.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="border-t border-zinc-800 pt-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Subtotal:</span>
                        <span className="text-zinc-300">€{invoice.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">TVSH ({((invoice.tvshRate ?? 0.18) * 100).toFixed(0)}%):</span>
                        <span className="text-zinc-300">€{invoice.tvshAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-base font-semibold">
                        <span className="text-zinc-400">Total:</span>
                        <span className="text-emerald-400">€{invoice.total.toFixed(2)}</span>
                      </div>
                      {invoice.cogsTotal != null && (
                        <div className="mt-2 pt-2 border-t border-zinc-800 space-y-1 text-sm" data-testid={`invoice-margin-${invoice.id}`}>
                          <div className="flex justify-between">
                            <span className="text-zinc-500">Cost of goods (raw steel):</span>
                            <span className="text-zinc-300">€{invoice.cogsTotal.toFixed(2)}</span>
                          </div>
                          {invoice.profitTotal != null && (
                            <div className="flex justify-between font-semibold">
                              <span className="text-zinc-400">Profit (pa TVSH):</span>
                              <span className={invoice.profitTotal >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                                {invoice.profitTotal >= 0 ? '+' : ''}€{invoice.profitTotal.toFixed(2)}
                                <span className="ml-1 text-[11px] text-zinc-500">
                                  ({((invoice.profitTotal / invoice.subtotal) * 100).toFixed(1)}% margin)
                                </span>
                              </span>
                            </div>
                          )}
                          {invoice.cogsCoverageIncomplete && (
                            <p className="text-[11px] text-amber-400/80">
                              ⚠ Partial cost coverage — some quantities had no matching production batch. Profit shown only for tracked portion.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {invoice.notes && (
                      <div className="mt-4 pt-3 border-t border-zinc-800 text-xs text-zinc-500">
                        <p>Notes: {invoice.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* In-app confirm dialog (replaces window.confirm) */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white"
              data-testid="confirm-dialog-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmDialog?.confirmVariant === 'destructive'
                  ? 'bg-rose-600 hover:bg-rose-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }
              onClick={async () => {
                const fn = confirmDialog?.onConfirm;
                setConfirmDialog(null);
                if (fn) await fn();
              }}
              data-testid="confirm-dialog-action"
            >
              {confirmDialog?.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Date picker dialog — ship / paid / edit-dates */}
      <AlertDialog open={!!dateDialog} onOpenChange={(open) => { if (!open) { setDateDialog(null); setDateError(null); setDateSubmitting(false); } }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="date-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{dateDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {dateDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            {(dateDialog?.fields || []).map((f) => (
              <div key={f.key} className="flex flex-col gap-1">
                <Label className="text-zinc-300 text-xs">{f.label}</Label>
                <Input
                  type="date"
                  value={dateValues[f.key] || ''}
                  onChange={(e) => setDateValues({ ...dateValues, [f.key]: e.target.value })}
                  min={f.min}
                  max={f.max}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100"
                  data-testid={`date-dialog-input-${f.key}`}
                />
              </div>
            ))}
            {dateError && (
              <div className="text-xs text-red-400 bg-red-950/30 border border-red-900/40 rounded p-2 whitespace-pre-line" data-testid="date-dialog-error">
                {dateError}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white"
              disabled={dateSubmitting}
              onClick={() => { setDateDialog(null); setDateError(null); }}
              data-testid="date-dialog-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={dateSubmitting || (dateDialog?.fields || []).some(f => !dateValues[f.key])}
              onClick={async (e) => {
                e.preventDefault();
                setDateError(null);
                setDateSubmitting(true);
                try {
                  await dateDialog.onConfirm(dateValues);
                  setDateDialog(null);
                } catch (err) {
                  setDateError(err.message || 'Error');
                } finally {
                  setDateSubmitting(false);
                }
              }}
              data-testid="date-dialog-action"
            >
              {dateSubmitting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Working…</> : (dateDialog?.confirmLabel || 'Confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvoicesPage;
